import { useState } from "react";
import { useTranslate, IResourceComponentsProps } from "@pankod/refine-core";
import {
  Edit,
  Form,
  Input,
  useForm,
} from "@pankod/refine-antd";
import { Switch, Button, Modal, Typography, Popconfirm, notification } from "antd";

import httpClient from "httpClient";
import { IServiceAccount } from "interfaces";

const { Text, Paragraph } = Typography;

export const ServiceAccountEdit: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();
  const [secretModalVisible, setSecretModalVisible] = useState(false);
  const [resetSecret, setResetSecret] = useState("");
  const [resetClientId, setResetClientId] = useState("");
  const [resetting, setResetting] = useState(false);

  const { formProps, saveButtonProps, id } = useForm<IServiceAccount>();

  const BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:8080";

  const handleResetSecret = async () => {
    setResetting(true);
    try {
      const response = await httpClient.post(
        `${BASE_URL}/api/v2/service-account/${id}/reset-secret`
      );
      const record = response.data;
      setResetClientId(record.client_id);
      setResetSecret(record.client_secret);
      setSecretModalVisible(true);
    } catch (error: any) {
      notification.error({
        message: t("serviceAccount.resetSecret.error", "Failed to reset secret"),
        description: error?.message,
      });
    } finally {
      setResetting(false);
    }
  };

  return (
    <>
      <Edit
        saveButtonProps={saveButtonProps}
        footerButtons={({ defaultButtons }) => (
          <>
            {defaultButtons}
            <Popconfirm
              title={t(
                "serviceAccount.resetSecret.confirm",
                "Are you sure? This will invalidate the current secret."
              )}
              onConfirm={handleResetSecret}
              okText={t("buttons.confirm", "Yes")}
              cancelText={t("buttons.cancel", "Cancel")}
            >
              <Button danger loading={resetting}>
                {t("serviceAccount.resetSecret.button", "Reset Client Secret")}
              </Button>
            </Popconfirm>
          </>
        )}
      >
        <Form {...formProps} layout="vertical">
          <Form.Item
            label={t("serviceAccount.fields.name")}
            name="name"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t("serviceAccount.fields.is_active")}
            name="is_active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Edit>

      <Modal
        title={t("serviceAccount.resetSecret.title", "Client Secret Reset")}
        visible={secretModalVisible}
        closable={false}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => setSecretModalVisible(false)}
          >
            {t("serviceAccount.secret.ok", "I've saved the credentials")}
          </Button>,
        ]}
      >
        <Paragraph>
          <Text strong>
            {t(
              "serviceAccount.secret.warning",
              "Save these credentials now. The client secret will not be shown again."
            )}
          </Text>
        </Paragraph>
        <Paragraph>
          <Text>{t("serviceAccount.fields.client_id", "Client ID")}: </Text>
          <Text code copyable>{resetClientId}</Text>
        </Paragraph>
        <Paragraph>
          <Text>{t("serviceAccount.fields.client_secret", "Client Secret")}: </Text>
          <Text code copyable>{resetSecret}</Text>
        </Paragraph>
      </Modal>
    </>
  );
};
